import { Injectable } from '@angular/core';
import { FilterByModalResult } from './filter-by-modal/filter-by-modal.component';

/**
 * Snapshot of the Resource Info table's user-controlled state. Held in
 * memory so that navigating Finance Details → Project History → Back
 * restores the operator's search / filter / sort / pagination context.
 *
 * Column visibility + togglable order are deliberately omitted — those
 * already persist via localStorage in FinanceDetailsComponent, with
 * different semantics (survive reloads, shared across tabs).
 */
export interface FinanceDetailsState {
  searchText: string;
  appliedFilters: FilterByModalResult;
  sortBy: string;
  sortDir: 'asc' | 'desc' | '';
  page: number;
  size: number;
}

/**
 * Single instance across the app (providedIn: 'root') — both FinanceDetails
 * and any future page that wants to round-trip the operator's working
 * context read/write this same store.
 *
 * Scope: in-memory only. A hard page reload (Ctrl-R, new tab) resets state
 * to defaults — that's intentional, since "fresh tab → fresh view" is the
 * less surprising default. If product wants survival across reloads, swap
 * the backing store for sessionStorage / localStorage.
 */
@Injectable({ providedIn: 'root' })
export class FinanceDetailsStateService {
  private state: FinanceDetailsState | null = null;

  /** Whether a snapshot has ever been saved this session. */
  hasState(): boolean {
    return this.state !== null;
  }

  /** Returns a defensive copy so callers can mutate without leaking. */
  getState(): FinanceDetailsState | null {
    if (!this.state) return null;
    return {
      searchText: this.state.searchText,
      appliedFilters: { ...this.state.appliedFilters },
      sortBy: this.state.sortBy,
      sortDir: this.state.sortDir,
      page: this.state.page,
      size: this.state.size,
    };
  }

  /** Overwrites the entire snapshot. Called from FinanceDetailsComponent
   *  on every load() call so the latest user choices are always preserved. */
  setState(next: FinanceDetailsState): void {
    this.state = {
      searchText: next.searchText,
      appliedFilters: { ...next.appliedFilters },
      sortBy: next.sortBy,
      sortDir: next.sortDir,
      page: next.page,
      size: next.size,
    };
  }

  /** Wipes the snapshot — used if we ever expose a "Clear session" action. */
  clear(): void {
    this.state = null;
  }
}
